import {
  DeviceSidebar,
  Grid,
  space,
  Tabs,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import { sendCommand } from "../api/laboperator.ts";
import type { CommandIdentify } from "../api/laboperator.types.ts";
import { updateDeviceName } from "../api/lads.ts";
import { useAccessToken } from "../auth-provider";
import { useDeviceImage } from "../hooks/use-device-image";
import { getIncidentsForDevice } from "../hooks/use-incidents";
import { useDevicesStore } from "../store/devices-store";
import type { Device, Incident, Unit } from "../view-model.ts";
import { DetailEvaluation } from "./detail-evaluation.tsx";
import { DeviceFunctions } from "./device-functions.tsx";
import { LadsDetailIncidents } from "./lads-detail-incidents.tsx";
import { LADSDeviceControl } from "./lads-device-control.tsx";

export type DetailProps = {
  className?: string;
  device: Device;
  units?: Unit[];
  incidents?: Incident[];
  onRefetchUnits?: () => void;
  renderDetailChannelValues: boolean;
  renderDetailEvaluation?: boolean;
  renderDetailIncidents?: boolean;
  renderDeviceControl?: boolean;
};

const StyledDetailPage = styledComponents.div`
  height: 100%;
  overflow: auto;
  & > * {
    height: 100%;
  }
`;

// Overlaid notification flag at the top-right corner of the tab label.
const TabLabelWithBadge = styledComponents.span`
  position: relative;
  display: inline-flex;
`;

const TabBadge = styledComponents.span<{ $critical: boolean }>`
  position: absolute;
  top: -3px;
  right: -15px;
  box-sizing: border-box;
  min-width: 14px;
  height: 14px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background-color: ${({ $critical, theme }) =>
    $critical ? (theme as any).color.error : (theme as any).color.warning};
  color: ${({ theme }) => (theme as any).color.white};
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
`;

const TabContentWrapper = styledComponents.div`
  border-left: 1px solid ${({ theme }) => (theme as any).colorPalette.grey300};
  height: 100%;
  overflow: auto;
  padding: ${space(3)};
  padding-right: 0;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
`;

const delay = (ms = 100) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const onLocateDevice = async (
  device: Device,
  token: string,
  setPulsing: { (value: SetStateAction<boolean>): void }
) => {
  const [deviceId, canId] = device.id.split("-");
  setPulsing(true);
  await sendCommand(token, deviceId, {
    data: {
      command: "set_ping",
      data: {
        CanId: Number(canId),
        Value: "on",
      },
    },
  } as CommandIdentify);
  await delay(10_000);
  await sendCommand(token, deviceId, {
    data: {
      command: "set_ping",
      data: {
        CanId: Number(canId),
        Value: "off",
      },
    },
  } as CommandIdentify);
  setPulsing(false);
};

const DeviceSidebarWithImageFallback: FC<
  {
    imageUrl: string;
  } & Omit<React.ComponentProps<typeof DeviceSidebar>, "imageUrl">
> = ({ imageUrl, ...props }) => {
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);

  useEffect(() => {
    // Check if image exists
    const img = new Image();

    const handleLoad = () => {
      setCurrentImageUrl(imageUrl);
    };

    const handleError = () => {
      setCurrentImageUrl("/devices/no-image.png");
    };

    img.addEventListener("load", handleLoad);
    img.addEventListener("error", handleError);
    img.src = imageUrl;

    return () => {
      img.removeEventListener("load", handleLoad);
      img.removeEventListener("error", handleError);
    };
  }, [imageUrl]);

  return <DeviceSidebar {...props} imageUrl={currentImageUrl} />;
};

export const DeviceDetail: FC<DetailProps> = ({
  device,
  incidents = [],
  onRefetchUnits = () => undefined,
  renderDetailChannelValues = true,
  renderDetailEvaluation = true,
  renderDetailIncidents = true,
  renderDeviceControl = true,
  units = [],
}) => {
  const deviceIncidents = getIncidentsForDevice(device, incidents, units);
  const { t } = useTranslation();
  // DeviceTypeImage from OPC-UA if present, else the static default image.
  const deviceImageUrl = useDeviceImage(device.deviceTypeImage, device.image);
  const hasActiveIncidents =
    deviceIncidents.errors.length > 0 || deviceIncidents.warnings.length > 0;
  // Critical wins for the badge color: as long as any critical incident is
  // active, the device-bild shows red; warning-only stays yellow.
  let deviceIncidentSeverity: "critical" | "warning" | undefined;
  if (deviceIncidents.errors.length > 0) {
    deviceIncidentSeverity = "critical";
  } else if (deviceIncidents.warnings.length > 0) {
    deviceIncidentSeverity = "warning";
  }
  // Calculate the correct initial tab index (errors tab is index 3: Data=0, Evaluation=1, Controls=2, Errors=3)
  const getInitialTabIndex = () => {
    if (hasActiveIncidents && renderDetailIncidents) {
      return 3;
    }
    return 0;
  };
  const [selectedOption, setSelectedOption] = useState<number>(
    getInitialTabIndex()
  );
  const [isPulsing, setPulsing] = useState(false);
  const [isTitleEditable, setIsTitleEditable] = useState(false);
  const [newTitle, setNewtitle] = useState(device?.name || "");
  const [savedTitle, setsavedTitle] = useState(device?.name || "");
  const [isTitleLoading, setisTitleLoading] = useState(false);
  const accessToken = useAccessToken();
  const { updateDevice: updateDeviceInStore } = useDevicesStore();

  // Update title states when device changes
  useEffect(() => {
    if (device?.name) {
      setNewtitle(device.name);
      setsavedTitle(device.name);
    }
  }, [device?.name]);

  const handleSwitch = (index: number) => {
    setSelectedOption(index);
  };

  const handleActionTitle = async (mode: "save" | "edit" | "cancel") => {
    if (mode === "edit") {
      setNewtitle(savedTitle);
      setIsTitleEditable(true);
    }
    if (mode === "save") {
      handleSave();
    }
    if (mode === "cancel") {
      setIsTitleEditable(false);
      // Reset to full name
      setNewtitle(savedTitle);
    }
  };

  const handleSave = async () => {
    setisTitleLoading(true);
    setIsTitleEditable(false);

    try {
      // All devices use LADS API
      if (!device.id) throw new Error("Device has no id — cannot set override");
      await updateDeviceName(
        device.id,
        "ComponentName",
        newTitle,
        accessToken ?? undefined
      );
      // ComponentName is the plain display name;
      setsavedTitle(newTitle);
      // Also update the store directly for immediate UI update (header, etc.)
      updateDeviceInStore(device.id, { name: newTitle });
    } catch {
      setsavedTitle(device.name || "");
    }
    setisTitleLoading(false);
  };

  const handleChangeTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewtitle(event.target.value);
  };

  return (
    <StyledDetailPage>
      <Grid columnGap="0" gridTenplateRows="100%">
        <Grid.Column width={3}>
          <DeviceSidebarWithImageFallback
            actionText={t("detail.action.text")}
            backActionText={t("detail.back.action.text")}
            connectionColor={
              device.state?.status === "online" ? "green500" : "grey300"
            }
            connectionText={
              t(`detail.connection.text.${device.state?.status}`) as string
            }
            connectionTitle={t("detail.connection.title") as string}
            editable={isTitleEditable}
            href={"/workplaces" + location.search}
            imageUrl={deviceImageUrl}
            incidentSeverity={deviceIncidentSeverity}
            isPulsing={isPulsing}
            loading={isTitleLoading}
            locationText={device.location.position || ""}
            locationTitle={t("detail.location.title")}
            manufacturerText={
              device.manufacturer || (t("data.missing.manufacturer") as string)
            }
            manufacturerTitle={t("detail.manufacturer.title") as string}
            modelText={device.model || (t("data.missing.model") as string)}
            modelTitle={t("detail.model.title") as string}
            onActionClick={() =>
              onLocateDevice(device, accessToken ?? "", setPulsing)
            }
            onActionTitle={handleActionTitle}
            onChangeTitle={handleChangeTitle}
            serialText={device.serial || t("data.missing.serial")}
            serialTitle={t("detail.serial.title")}
            stateMessages={device.state?.messages?.map((messageObject) =>
              t(messageObject.message)
            )}
            title={
              isTitleEditable ? newTitle : savedTitle || t("data.missing.name")
            }
          />
        </Grid.Column>
        <Grid.Column width={9}>
          <Tabs active={selectedOption} onSwitch={handleSwitch}>
            {renderDetailChannelValues ? (
              <Tabs.Item key="data" title={t("detail.tab.data")}>
                <TabContentWrapper>
                  <DeviceFunctions
                    deviceStatus={device.state?.status ?? "offline"}
                    incidents={[
                      ...deviceIncidents.errors,
                      ...deviceIncidents.warnings,
                    ]}
                    onRefetch={onRefetchUnits}
                    units={units}
                  />
                </TabContentWrapper>
              </Tabs.Item>
            ) : (
              <br />
            )}
            {renderDetailEvaluation ? (
              <Tabs.Item key="evaluation" title={t("detail.tab.evaluation")}>
                <TabContentWrapper>
                  <DetailEvaluation units={units} />
                </TabContentWrapper>
              </Tabs.Item>
            ) : (
              <br />
            )}
            {renderDeviceControl ? (
              <Tabs.Item key="controls" title={t("detail.tab.controls")}>
                <TabContentWrapper>
                  <LADSDeviceControl device={device} />
                </TabContentWrapper>
              </Tabs.Item>
            ) : (
              <br />
            )}
            {renderDetailIncidents ? (
              <Tabs.Item
                key="errors"
                title={
                  hasActiveIncidents ? (
                    <TabLabelWithBadge>
                      {t("detail.tab.errors")}
                      <TabBadge
                        $critical={deviceIncidentSeverity === "critical"}
                      >
                        {deviceIncidents.errors.length +
                          deviceIncidents.warnings.length}
                      </TabBadge>
                    </TabLabelWithBadge>
                  ) : (
                    t("detail.tab.errors")
                  )
                }
              >
                <TabContentWrapper>
                  <LadsDetailIncidents device={device} units={units} />
                </TabContentWrapper>
              </Tabs.Item>
            ) : (
              <br />
            )}
          </Tabs>
        </Grid.Column>
      </Grid>
    </StyledDetailPage>
  );
};
